import Promise from 'bluebird';
import queryString from 'query-string';
import { push } from 'react-router-redux';
import { getCookie } from 'redux-cookie';
import SubmissionError from 'redux-form/lib/SubmissionError';
import { SESSION_NAME } from '../helpers/serverRenderingUtils';
import { onLogIn, onLogOut } from './modules/analytics';
import {
  continueOnboardingAfterAuthentication,
  getFeedFromLocation,
} from './onboarding';
import { RECEIVE_PODCAST_METADATA } from './podcastEditor';
import AnchorApi from './modules/AnchorAPI';

// constants
const MINIMUM_PASSWORD_CHARACTER_COUNT = 7;
const VALID_EMAIL_REGEX = /^\S+@\S+$/;

export const EMAIL_ERROR_MESSAGE = 'A valid email address is required.';
export const EXISTING_EMAIL_ERROR_MESSAGE = 'Existing email found';
export const EXISTING_ACCOUNT_ERROR_MESSAGE = 'Existing account found';
export const FRIENDLY_ERROR_MESSAGE = `Sorry, but we were unable to create your account. Please email us at help@anchor.fm and we'll help you troubleshoot.`;
export const NAME_ERROR_MESSAGE = 'A name is required.';
export const PASSWORD_ERROR_MESSAGE = 'Passwords must be 7 characters or more.';

const SET_USER = '@@user/SET_USER';
const UNSET_USER = '@@user/UNSET_USER';
const TOGGLE_ACCOUNT_MENU = '@@user/TOGGLE_ACCOUNT_MENU';
const OPEN_ACCOUNT_MENU = '@@user/OPEN_ACCOUNT_MENU';
const CLOSE_ACCOUNT_MENU = '@@user/CLOSE_ACCOUNT_MENU';
const SHOW_CREATE_ACCOUNT = '@@user/SHOW_CREATE_ACCOUNT';
const SHOW_LOGIN = '@@user/SHOW_LOGIN';
const SHOW_REQUEST_RESET_PASSWORD = '@@user/SHOW_REQUEST_RESET_PASSWORD';
const SHOW_REQUEST_RESET_PASSWORD_CONFIRMATION =
  '@@user/SHOW_REQUEST_RESET_PASSWORD_CONFIRMATION';
const SET_ENABLED_FEATURE_FLAGS = '@@user/SET_ENABLED_FEATURE_FLAGS';
const SET_PERMISSIONS = '@@user/SET_PERMISSIONS';
const SET_PODCAST_NETWORK = '@@user/SET_PODCAST_NETWORK';
const RECEIVE_REFERRAL_CODE = '@@user/RECEIVE_REFERRAL_CODE';
const RECEIVE_CALL_JOIN_CODE = '@@user/RECEIVE_CALL_JOIN_CODE';
const RECEIVE_HOST_NAME = '@@user/RECEIVE_HOST_NAME';
const RECEIVE_SHARE_LINK_PATH = '@@user/RECEIVE_SHARE_LINK_PATH';
const RECEIVE_COHOST_TOPIC_NAME = '@@user/RECEIVE_COHOST_TOPIC_NAME';

export const MENU_MODE = {
  LOGIN: 'login',
  CREATE_ACCOUNT: 'create',
  REQUEST_RESET_PASSWORD: 'requestResetPassword',
  REQUEST_RESET_PASSWORD_CONFIRMATION: 'requestResetPasswordConfirmation',
};

export const FEATURE_FLAGS = {
  SPONSORSHIPS: 'sponsorships',
  APPLE_MANUAL_DISTRIBUTION: 'applemanualdistribution',
};

const initialState = {
  callJoinCode: null,
  cohostTopicName: null,
  isAccountMenuOpen: false,
  user: null,
  stationId: null,
  hasArchivedWaves: false,
  hostName: null,
  menuMode: MENU_MODE.LOGIN,
  permissions: null,
  podcastNetwork: null,
  referralCode: null,
  shareLinkPath: null,
  shareLinkEmbedPath: null,
  enabledFeatureFlags: null,
};

// reducer

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case UNSET_USER: {
      return {
        ...state,
        user: null,
      };
    }
    case SET_USER: {
      const { user, stationId, hasArchivedWaves } = action;
      return {
        ...state,
        user,
        stationId,
        hasArchivedWaves,
        menuMode: MENU_MODE.LOGIN, // if a user was set, we should default back to log in popup
      };
    }
    case SET_ENABLED_FEATURE_FLAGS:
      return {
        ...state,
        enabledFeatureFlags: action.payload,
      };
    case SET_PERMISSIONS: {
      const { permissions } = action;
      return {
        ...state,
        permissions,
      };
    }
    case SET_PODCAST_NETWORK: {
      return {
        ...state,
        podcastNetwork: action.payload,
      };
    }
    case OPEN_ACCOUNT_MENU: {
      return {
        ...state,
        isAccountMenuOpen: true,
      };
    }
    case CLOSE_ACCOUNT_MENU: {
      return {
        ...state,
        isAccountMenuOpen: false,
      };
    }
    case TOGGLE_ACCOUNT_MENU: {
      const { isAccountMenuOpen } = state;
      return {
        ...state,
        isAccountMenuOpen: !isAccountMenuOpen,
      };
    }
    case SHOW_CREATE_ACCOUNT: {
      return {
        ...state,
        menuMode: MENU_MODE.CREATE_ACCOUNT,
      };
    }
    case SHOW_LOGIN: {
      return {
        ...state,
        menuMode: MENU_MODE.LOGIN,
      };
    }
    case SHOW_REQUEST_RESET_PASSWORD: {
      return {
        ...state,
        menuMode: MENU_MODE.REQUEST_RESET_PASSWORD,
      };
    }
    case SHOW_REQUEST_RESET_PASSWORD_CONFIRMATION: {
      return {
        ...state,
        menuMode: MENU_MODE.REQUEST_RESET_PASSWORD_CONFIRMATION,
      };
    }
    case RECEIVE_CALL_JOIN_CODE: {
      return {
        ...state,
        callJoinCode: action.callJoinCode,
      };
    }
    case RECEIVE_REFERRAL_CODE: {
      return {
        ...state,
        referralCode: action.referralCode,
      };
    }
    case RECEIVE_COHOST_TOPIC_NAME: {
      return {
        ...state,
        cohostTopicName: action.cohostTopicName,
      };
    }
    case RECEIVE_HOST_NAME: {
      return {
        ...state,
        hostName: action.payload.hostName,
      };
    }
    case RECEIVE_SHARE_LINK_PATH: {
      return {
        ...state,
        shareLinkPath: action.payload.shareLinkPath,
        shareLinkEmbedPath: action.payload.shareLinkEmbedPath,
      };
    }
    case RECEIVE_PODCAST_METADATA: {
      return {
        ...state,
        user: {
          ...state.user,
          name: action.payload.authorName,
        },
      };
    }
    default:
      return state;
  }
}

// action creators

export function setUser({
  imageUrl,
  userId,
  name,
  stationId,
  hasArchivedWaves,
}) {
  return {
    type: SET_USER,
    user: {
      userId,
      name,
      imageUrl,
    },
    stationId,
    hasArchivedWaves,
  };
}

export function unsetUser() {
  return {
    type: UNSET_USER,
  };
}

export function openAccountMenu() {
  return {
    type: OPEN_ACCOUNT_MENU,
  };
}

export function closeAccountMenu() {
  return {
    type: CLOSE_ACCOUNT_MENU,
  };
}

export function clickAccount(target) {
  return {
    type: TOGGLE_ACCOUNT_MENU,
    meta: {
      analytics: {
        type: 'event-account-click',
        payload: {
          target,
        },
      },
    },
  };
}

export function submitLoginFormEvent() {
  return {
    type: '', // no actual actions
    meta: {
      analytics: {
        type: 'event-account-submit',
        payload: {
          target: 'Log In Form',
        },
      },
    },
  };
}

function submitCreateAccountFormEvent() {
  return {
    type: '', // no actual actions
    meta: {
      analytics: {
        type: 'event-account-submit',
        payload: {
          target: 'Create Account Form',
        },
      },
    },
  };
}

function submitRequestResetPasswordFormEvent() {
  return {
    type: '', // no actual actions
    meta: {
      analytics: {
        type: 'event-account-submit',
        payload: {
          target: 'Forgot Password Form',
        },
      },
    },
  };
}

export function showCreateAccount(target) {
  return {
    type: SHOW_CREATE_ACCOUNT,
    meta: {
      analytics: {
        type: 'event-account-click',
        payload: {
          target,
        },
      },
    },
  };
}

export function showLogin(target) {
  return {
    type: SHOW_LOGIN,
    meta: {
      analytics: {
        type: 'event-account-click',
        payload: {
          target,
        },
      },
    },
  };
}

export function showRequestResetPassword(target) {
  return {
    type: SHOW_REQUEST_RESET_PASSWORD,
    meta: {
      analytics: {
        type: 'event-account-click',
        payload: {
          target,
        },
      },
    },
  };
}

export function showRequestResetPasswordConfirmation() {
  return {
    type: SHOW_REQUEST_RESET_PASSWORD_CONFIRMATION,
  };
}

function setPodcastNetwork(network) {
  return {
    type: SET_PODCAST_NETWORK,
    payload: network,
  };
}

function setPermissions(permissions) {
  return {
    type: SET_PERMISSIONS,
    permissions,
  };
}

function setEnabledFeatureFlags(enabledFeatureFlags) {
  return {
    type: SET_ENABLED_FEATURE_FLAGS,
    payload: enabledFeatureFlags,
  };
}

function receiveShareLinkPath(payload) {
  return {
    type: RECEIVE_SHARE_LINK_PATH,
    payload,
  };
}

export function receiveCallJoinCode(callJoinCode) {
  return {
    type: RECEIVE_CALL_JOIN_CODE,
    callJoinCode,
  };
}

export function receiveReferralCode(referralCode) {
  return {
    type: RECEIVE_REFERRAL_CODE,
    referralCode,
  };
}

function receiveCohostTopicName(cohostTopicName) {
  return {
    type: RECEIVE_COHOST_TOPIC_NAME,
    cohostTopicName,
  };
}

export function receiveHostName(hostName) {
  return {
    type: RECEIVE_HOST_NAME,
    payload: {
      hostName,
    },
  };
}

// thunks

export function requestToLogOutUser() {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      fetch('/api/logout', {
        method: 'POST',
        credentials: 'same-origin',
      })
        .then(resolve)
        .catch(reject);
    }).finally(() => {
      dispatch(checkAuthentication());
      dispatch(closeAccountMenu());
    });
}

export function checkAuthentication(baseUrl = '') {
  return (dispatch, getState) => {
    // fetch user auth, then check cookie
    return getCurrentUser(baseUrl).then(({ user }) => {
      const sessionCookie = dispatch(getCookie(SESSION_NAME));

      if (sessionCookie && user) {
        const {
          user: { permissions, shareLinkPath, enabledFeatureFlags },
          routing: { location },
        } = getState();
        const query = location && queryString.parse(location.search);
        const promises = [];
        if (!shareLinkPath) {
          promises.push(dispatch(fetchShareLinkPath(baseUrl)));
        }
        if (!permissions) {
          promises.push(dispatch(fetchPermissions(baseUrl)));
        }
        if (!enabledFeatureFlags) {
          promises.push(dispatch(fetchAndSetAllEnabledFeatureFlags(baseUrl)));
        }
        if (promises.length) {
          return Promise.all(promises).then(() => {
            setUserAndRedirect(user, query);
          });
        }
        setUserAndRedirect(user, query);
        return;
      }
      unsetUserAndRedirect(user);
    });

    function setUserAndRedirect(user, query) {
      dispatch(setUser(user));
      // analytics
      onLogIn(user);

      if (query && query.return_to) {
        // use regular redirect as some routes are handled by API (e.g. Stripe)
        location.href = query.return_to;
        return;
      }

      // a bit of a hack, but put something on the history stack
      // to indicate to our router onEnter HOC that something has
      // changed, so it re-evaluates whatever handler is present
      dispatch(push({ key: 'foo' }));
    }
    function unsetUserAndRedirect(user) {
      dispatch(unsetUser());
      // a bit of a hack, but put something on the history stack
      // to indicate to our router onEnter HOC that something has
      // changed, so it re-evaluates whatever handler is present
      dispatch(push({ key: 'foo' }));

      // analytics
      onLogOut(user);
    }
  };
}

export function submitLogInForm(data) {
  return (dispatch, getState) => {
    dispatch(submitLoginFormEvent());
    const { onboarding, routing } = getState();
    const { location } = routing;
    return fetch('/api/login', {
      method: 'POST',
      credentials: 'same-origin',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({
        // pass beta code in case this is part of onboarding
        betaCode: onboarding.validBetaCode,
        // pass RSS feed in case this is part of onboarding
        feedUrl: getFeedFromLocation(location, true),
        ...data,
      }),
    }).then(response => {
      const { status } = response;
      if (status === 400 || status === 401) {
        return response.text().then(errorText => {
          if (errorText) {
            throw new SubmissionError({ _error: errorText });
          }
          // generic response for a 401 unauthorized
          throw new SubmissionError({
            _error: 'There was a problem with this email or password.',
          });
        });
      }
      dispatch(checkAuthentication()).then(() => {
        dispatch(continueOnboardingAfterAuthentication());
      });
      const {
        routing: { location },
      } = getState();
      const query = location && queryString.parse(location.search);
      // this implies that we did not come from a dedicated login page, so pop open the menu
      if (query && !query.return_to) {
        dispatch(openAccountMenu());
      }
    });
  };
}

function isValidEmail(email) {
  return VALID_EMAIL_REGEX.test(email);
}

function isValidPassword(password) {
  return password.length >= MINIMUM_PASSWORD_CHARACTER_COUNT;
}

function clientErrors(data) {
  const { email, password } = data;
  const errors = [];
  if (email && !isValidEmail(email)) {
    errors.push(EMAIL_ERROR_MESSAGE);
  }
  if (password && !isValidPassword(password)) {
    errors.push(PASSWORD_ERROR_MESSAGE);
  }
  return errors;
}

export function submitCreateAccountForm(data) {
  return (dispatch, getState) => {
    const { onboarding } = getState();
    dispatch(submitCreateAccountFormEvent());
    const errors = clientErrors(data);
    if (errors.length > 0) {
      throw new SubmissionError({ _error: errors.join(' ') });
    }
    return fetch('/api/user/account/', {
      method: 'POST',
      credentials: 'same-origin',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({
        vanitySlug: data.name,
        betaCode: onboarding.validBetaCode,
        feedUrl: getFeedFromLocation(location, true),
        ...data,
      }),
    }).then(response => {
      const { status: statusCode } = response;
      // TODO: Sometimes a successful account creation returns 404... why??
      if (response.ok) {
        dispatch(checkAuthentication()).then(() => {
          dispatch(continueOnboardingAfterAuthentication());
        });
        dispatch(openAccountMenu());
      } else {
        switch (statusCode) {
          case 409:
            throw new SubmissionError({
              _error: EXISTING_EMAIL_ERROR_MESSAGE,
            });
          case 410:
            throw new SubmissionError({
              _error: EXISTING_ACCOUNT_ERROR_MESSAGE,
            });
          case 500:
          default:
            return response.text().then(errorText => {
              if (errorText) {
                throw new SubmissionError({ _error: FRIENDLY_ERROR_MESSAGE });
              }
            });
        }
      }
    });
  };
}

export function submitRequestResetPasswordForm(data) {
  return (dispatch, getState) => {
    dispatch(submitRequestResetPasswordFormEvent());
    return fetch('/api/requestresetpassword/', {
      method: 'POST',
      credentials: 'same-origin',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(data),
    }).then(response => {
      const { status } = response;
      if (status === 400) {
        throw new SubmissionError({
          _error: 'There was a problem with this email.',
        });
      }
      // assume 200 ok
      dispatch(showRequestResetPasswordConfirmation());
    });
  };
}

export function fetchPermissions(baseUrl = '') {
  return (dispatch, getState) =>
    fetch(`${baseUrl}/api/permissions`, {
      credentials: 'same-origin',
    })
      .then(response => {
        const { status } = response;
        if (status === 200) {
          return response.json();
        }
        return null;
      })
      .then(responseJson => {
        if (responseJson && responseJson.permissions) {
          dispatch(setPermissions(responseJson.permissions));
        }
        if (responseJson && responseJson.podcastNetwork) {
          dispatch(setPodcastNetwork(responseJson.podcastNetwork));
        }
      });
}

function fetchAndSetAllEnabledFeatureFlags(baseUrl = '') {
  return (dispatch, getState) =>
    AnchorApi.fetchAllEnabledFeatureFlags(baseUrl).then(response => {
      if (response.allEnabledFlags) {
        dispatch(setEnabledFeatureFlags(response.allEnabledFlags));
      }
    });
}

export function unsetAndRedirectUser(url) {
  return dispatch => {
    dispatch(unsetUser());
    dispatch(push(url || '/login'));
  };
}

function fetchShareLinkPath(baseUrl = '') {
  return (dispatch, getState) =>
    fetch(`${baseUrl}/api/user/sharelinkpath`, {
      credentials: 'same-origin',
    })
      .then(response => {
        const { status } = response;
        if (status === 200) {
          return response.json();
        }
        return null;
      })
      .then(responseJson => {
        if (responseJson && responseJson.shareLinkPath) {
          dispatch(receiveShareLinkPath(responseJson));
        }
      });
}

export function fetchCohostTopicName(baseUrl = '', webCohostTopicId) {
  return (dispatch, getState) =>
    fetch(`${baseUrl}/api/call/cohosttopic/${webCohostTopicId}`, {
      credentials: 'same-origin',
    })
      .then(response => {
        const { status } = response;
        if (status === 200) {
          return response.json();
        }
        return null;
      })
      .then(responseJson => {
        if (responseJson && responseJson.name) {
          dispatch(receiveCohostTopicName(responseJson.name));
        }
      });
}

// misc

function getCurrentUser(baseUrl = '') {
  // add timestamp query param to cache bust IE11 as all XHR seem cached
  // very annoying as this does NOT happen when debugging with dev tools...
  return fetch(`${baseUrl}/api/currentuser?${Date.now()}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(response => response.json());
}

// errors shape matches values input
export function validate(values) {
  const { name, email, password, captcharesponse } = values;
  const errors = {};
  if (!captcharesponse) {
    errors.captcharesponse = true;
  }
  if (!password) {
    errors.password = true;
  }
  if (!email) {
    errors.email = true;
  }
  if (!name) {
    errors.name = true;
  }
  return errors;
}

// errors shape matches values input
export function validateEmail(values) {
  const errors = {};
  if (!values.email) {
    errors.email = 'An email address is required';
  }
  return errors;
}
